import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { each } from 'bluebird';
import path from 'path';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { v4 as uuidV4 } from 'uuid';
import Label from './label';
import Loader from '../../common/loader';
import fileProcessor from '../../../utils/fileProcessor';
import fileSizeValidator from '../../../utils/fileSizeValidator';
import uploadUtils from '../../../utils/uploadUtils';
import entityComposition from '../../common/classes/entityComposition';
import formActionCreators from '../../../actions/formActionCreators';
import formModalActionCreators from '../../../actions/formModalActionCreators';
import store from '../../../reducers/storeRegistration';
import * as ajax from '../../../actions/ajax';
import loginActions from '../../../actions/loginActionCreators';
const validateMultipleFiles = fileSizeValidator.validateMultipleFiles;

// this is the only video file type supported by the transcoder
const videoTargetFormat = 'mp4';
const actionCreators = {
  formActionCreators,
  // only form currently supports the editor cache
  formModalActionCreators
};
const UploadField = _ref => {
  let notificationActions = _ref.notificationActions,
    _ref$postActions = _ref.postActions,
    actions = _ref$postActions === void 0 ? [] : _ref$postActions,
    permittedFileTypes = _ref.permittedFileTypes,
    reduxEntity = _ref.reduxEntity,
    onChange = _ref.onChange,
    _ref$enabled = _ref.enabled,
    enabled = _ref$enabled === void 0 ? true : _ref$enabled,
    _ref$formActions = _ref.formActions,
    formActions = _ref$formActions === void 0 ? {} : _ref$formActions,
    _ref$maxFiles = _ref.maxFiles,
    maxFiles = _ref$maxFiles === void 0 ? 100 : _ref$maxFiles,
    _ref$parentContentPat = _ref.parentContentPath,
    parentContentPath = _ref$parentContentPat === void 0 ? null : _ref$parentContentPat,
    _ref$contentPath = _ref.contentPath,
    contentPath = _ref$contentPath === void 0 ? null : _ref$contentPath,
    _ref$cachedVariableNa = _ref.cachedVariableName,
    cachePath = _ref$cachedVariableNa === void 0 ? null : _ref$cachedVariableNa,
    _ref$instructions = _ref.instructions,
    instructions = _ref$instructions === void 0 ? 'Please drop a file here or click to select a file to upload.' : _ref$instructions,
    _ref$title = _ref.title,
    title = _ref$title === void 0 ? 'Upload Dropzone' : _ref$title,
    _ref$variables = _ref.variables,
    variables = _ref$variables === void 0 ? {} : _ref$variables,
    _ref$maxFileSize = _ref.maxFileSize,
    maxFileSize = _ref$maxFileSize === void 0 ? {} : _ref$maxFileSize,
    _ref$highResolutionIm = _ref.highResolutionImages,
    highResolutionImages = _ref$highResolutionIm === void 0 ? false : _ref$highResolutionIm;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    uploading = _useState2[0],
    setUploading = _useState2[1];

  // Cleanup function to ensure state updates won't run if uploadField is no longer visible
  // ex. postActions may cause the field to no longer be mounted, state updates would be a leak
  useEffect(() => () => {
    setUploading(false);
  }, []);

  /**
   * Determines the file type category based on MIME type
   * @param {string} fileType - The MIME type of the file
   * @returns {string} The file type category ('video', 'pdf', 'image', or 'document')
   */
  const getFileType = useCallback(fileType => {
    if (fileType.includes('video')) return 'video';
    if (fileType.includes('pdf')) return 'pdf';
    if (fileType.includes('image')) return 'image';
    return 'document'; // fallback for other file types like CSV, TXT, etc.
  }, []);

  /**
   * Handles file drop events, processes uploads, and manages transcoding
   * @param {File[]} acceptedFiles - Array of accepted files to process
   * @param {File[]} rejectedFiles - Array of rejected files
   */
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    const uploadedFiles = {};
    const processedFiles = {};

    // handle errors, either too many files, the wrong type of files
    // or other types of dropzone or file type issues
    if (acceptedFiles.length > maxFiles || rejectedFiles.length) {
      const validExt = [];
      Object.keys(permittedFileTypes).forEach(key => {
        const next = permittedFileTypes[key];
        const newExtensions = next.length ? next : [".".concat(key.split('/')[1])];
        validExt.push(...newExtensions);
      });
      const rejectionMessage = preface => "".concat(preface, ". Please try again with ").concat(maxFiles === 1 ? 'a single file' : "no more then ".concat(maxFiles, " valid files"), " of type ").concat(validExt.join(', '), ".");
      notificationActions.error({
        message: rejectionMessage(rejectedFiles.length ? 'One or more files were rejected' : 'Too many files selected')
      }, {
        autoClose: 8000
      });
      return;
    }

    // at the moment, we only care about pdf file types for encryption and file type validation
    if (Object.keys(permittedFileTypes).includes('application/pdf')) {
      try {
        await each(acceptedFiles, async file => {
          if (file.type === 'application/pdf') {
            try {
              await fileProcessor.detectFileAndCreateMD5Hash(file);
            } catch (err) {
              throw err instanceof Error ? err : new Error(err);
            }
          }
        });
      } catch (error) {
        notificationActions.error({
          message: error.message,
          id: 'invalid_file_type'
        }, {
          autoClose: 10000
        });
        return;
      }
    }

    // Validate file sizes against MIME type restrictions
    const fileSizeValidation = validateMultipleFiles(acceptedFiles, maxFileSize);
    if (!fileSizeValidation.isValid) {
      notificationActions.error({
        message: fileSizeValidation.errorMessage,
        id: 'file_size_exceeded'
      }, {
        autoClose: 20000
      });
      return;
    }

    // start the processing spinner while files are being uploaded
    // don't stop till post actions complete
    await setUploading(true);

    // iterate over the accepted files and:
    // 1. retrieve a tmp upload url from the server
    // 2. upload the file
    // 3. retrieve a short term public url for the uploaded file
    // 4. create two objects, one an associated doc object and another the same but w/the raw file
    await each(acceptedFiles, async file => {
      const uploadFileExtension = path.extname(file.name).toLowerCase().slice(1);
      const baseFileName = uuidV4().toUpperCase();
      const fileRemotePath = "".concat(baseFileName, ".").concat(uploadFileExtension);

      // Check if this is an image file that needs compression
      const isImageFile = file.type.startsWith('image/');
      if (isImageFile) {
        // Handle image files with compression and optional high-resolution upload
        try {
          // Upload image with compression handled internally
          const uploadResult = await uploadUtils.uploadImage({
            file,
            baseFileName,
            highResEnabled: highResolutionImages,
            maxFileSize
          });

          // Check for errors
          if (uploadResult.errors.length > 0) {
            const hasBlockingErrors = uploadResult.errors.some(error => error.includes('Upload failed') || error.includes('exceeds the maximum allowed size'));
            if (hasBlockingErrors) {
              // Blocking error - show error and skip this file
              notificationActions.error({
                message: uploadResult.errors.join(' '),
                id: 'image_upload_failed'
              }, {
                autoClose: 10000
              });
              return;
            }
            // Non-blocking errors (like low-res upload failure) - show warning but continue
            notificationActions.error({
              message: uploadResult.errors.join(' '),
              id: 'image_low_upload_warning'
            }, {
              autoClose: 8000
            });
          }

          // Use the simplified remotePath directly
          const fileKey = uuidV4();
          uploadedFiles[fileKey] = {
            remotePath: uploadResult.remotePath,
            fileType: getFileType(file.type),
            _created: new Date().toISOString(),
            displayName: file.name
          };
          processedFiles[fileKey] = _objectSpread(_objectSpread({}, uploadedFiles[fileKey]), {}, {
            file
          });
          return;
        } catch (error) {
          notificationActions.error({
            message: "Failed to process image ".concat(file.name, ": ").concat(error.message),
            id: 'image_processing_failed'
          }, {
            autoClose: 10000
          });
          return;
        }
      }

      // Handle non-image files (existing logic)
      // Only supporting transcoding of video at the moment
      const transcode = file.type.startsWith('video/') && file.type !== "video/".concat(videoTargetFormat);

      // Get upload URL with token error handling
      let uploadURLRequest;
      try {
        uploadURLRequest = await ajax.get('/files/uploadUrl', {
          fileName: fileRemotePath,
          contentType: file.type
        }, 'text');
      } catch (error) {
        var _error$response;
        // If token expired (401), refresh and retry once
        if (((_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.status) === 401) {
          try {
            await store.dispatch(loginActions.serverToken());
            uploadURLRequest = await ajax.get('/files/uploadUrl', {
              fileName: fileRemotePath,
              contentType: file.type
            }, 'text');
          } catch (retryError) {
            // Retry failed, fall through to error handling
          }
        }
        if (!uploadURLRequest) {
          notificationActions.error({
            message: "Failed to get upload URL for ".concat(file.name, ". Please try again."),
            id: 'upload_url_failed'
          }, {
            autoClose: 10000
          });
          return; // Skip to next file
        }
      }

      // Upload file with token error handling
      try {
        await ajax.put(uploadURLRequest.data, file, file.type, 'text', false, false, false);
        // post this notification when in development and the video type is not mp4
        if (process.env.NODE_ENV === 'development' && file.type.startsWith('video/') && uploadFileExtension !== videoTargetFormat) {
          notificationActions.error({
            message: "Only ".concat(videoTargetFormat, " files are supported during local testing. Your file type was accepted, but it will not be transcoded and may not playback (unless it's an mov)")
          }, {
            autoClose: 8000
          });
        }
      } catch (error) {
        // S3 PUT failed - no retry since this is an external service
        notificationActions.error({
          message: "S3 upload failed for ".concat(file.name, ". Please try again."),
          id: 'upload_failed'
        }, {
          autoClose: 10000
        });
        return; // Skip to next file
      }

      // Get download URL with token error handling
      // if this is a video that needs transcoding, the video likely won't have been transcoded yet
      // not sure if AWS allows you to generate a downloadURL for a file that doesn't exist yet
      let cacheDownloadURL;
      try {
        const downloadResponse = await ajax.get('/files/downloadUrl', {
          fileName: fileRemotePath
        }, 'text');
        cacheDownloadURL = downloadResponse.data;
      } catch (error) {
        var _error$response2;
        // If token expired (401), refresh and retry once
        if (((_error$response2 = error.response) === null || _error$response2 === void 0 ? void 0 : _error$response2.status) === 401) {
          try {
            await store.dispatch(loginActions.serverToken());
            const downloadResponse = await ajax.get('/files/downloadUrl', {
              fileName: fileRemotePath
            }, 'text');
            cacheDownloadURL = downloadResponse.data;
          } catch (retryError) {
            // Retry failed, fall through to error handling
          }
        }
        if (!cacheDownloadURL) {
          notificationActions.error({
            message: "Failed to get download URL for ".concat(file.name, ". File was uploaded but may not display correctly."),
            id: 'download_url_failed'
          }, {
            autoClose: 10000
          });
          // Continue processing - file was uploaded, just can't get download URL
        }
      }

      // For videos that need transcoding, the _download path points to the transcoded version
      // while the original file is uploaded with its original extension
      const remotePath = {
        _download: transcode ? "".concat(baseFileName, ".").concat(videoTargetFormat) : fileRemotePath
      };

      // If the file wasn't transcoded and we have a download URL, populate the _cache path
      // so the user doesn't have to refresh to see their file displayed. If transcoding
      // is required, the file won't have yet been converted and saved to the remotePath yet
      /* eslint-disable-next-line no-underscore-dangle */
      if (!transcode && cacheDownloadURL) remotePath._cache = cacheDownloadURL;
      const fileKey = uuidV4();
      uploadedFiles[fileKey] = {
        remotePath,
        fileType: getFileType(file.type),
        _created: new Date().toISOString(),
        displayName: file.name
      };

      // save the original file extension for historical purposes
      if (transcode) uploadedFiles[fileKey].originalUploadType = uploadFileExtension;
      processedFiles[fileKey] = _objectSpread(_objectSpread({}, uploadedFiles[fileKey]), {}, {
        file
      });
    });

    // if contentPath passed in, save the assocaited doc object to content
    if (contentPath) {
      // save the uploaded files along with their remotePath,
      // display name and _cache so clients can render the file if desired
      await onChange({
        path: contentPath,
        value: uploadedFiles
      });
    }

    // if cachePath passed in, save the associated doc object + raw file to cache
    if (cachePath) {
      // save the files to the cache path in order to access in the post actions
      const entityActions = actionCreators["".concat(reduxEntity, "ActionCreators")];
      await store.dispatch(entityActions.setEditorCacheValue(cachePath, processedFiles));
    }

    // trigger post actions we can chain file parsing and other actions
    // The request is passed through formActions for all entity types since they all use
    // contentChangeActionFactory.handleActions
    await formActions.handleActions(reduxEntity, actions, parentContentPath, variables);
    await setUploading(false);
  }, [actions, cachePath, contentPath, formActions, getFileType, maxFiles, notificationActions, onChange, parentContentPath, permittedFileTypes, reduxEntity, variables, maxFileSize, highResolutionImages]);

  // return null if field is disabled
  if (!enabled) return null;
  return /*#__PURE__*/React.createElement(Loader, {
    loaded: !uploading,
    failedMessage: "File upload failed."
  }, /*#__PURE__*/React.createElement(Label, {
    value: title
  }), /*#__PURE__*/React.createElement(Dropzone, {
    className: "t-dud-dropzone",
    onDrop: onDrop,
    accept: permittedFileTypes,
    maxFiles: maxFiles
  }, _ref2 => {
    let getInputProps = _ref2.getInputProps,
      getRootProps = _ref2.getRootProps;
    return /*#__PURE__*/React.createElement("div", _extends({}, getRootProps(), {
      className: "dropzone t-u-dropzone"
    }), /*#__PURE__*/React.createElement("input", _extends({
      alt: "file-input",
      "data-testid": "drop-input"
    }, getInputProps())), instructions);
  }));
};
UploadField.propTypes = {
  formActions: PropTypes.shape({
    handleActions: PropTypes.func
  }),
  instructions: PropTypes.string,
  maxFiles: PropTypes.number,
  enabled: PropTypes.bool,
  title: PropTypes.string,
  contentPath: PropTypes.string,
  cachedVariableName: PropTypes.string,
  permittedFileTypes: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  postActions: PropTypes.arrayOf(PropTypes.shape({})),
  reduxEntity: PropTypes.string.isRequired,
  parentContentPath: PropTypes.string,
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired,
  variables: PropTypes.shape({}),
  maxFileSize: PropTypes.shape({}),
  highResolutionImages: PropTypes.bool
};
export { UploadField as TestUploadField };
export default entityComposition(UploadField, ['settings'], {
  maxFileSize: state => {
    var _state$settings;
    return (_state$settings = state.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.configuration) === null || _state$settings === void 0 ? void 0 : _state$settings.maxFileSize;
  },
  highResolutionImages: state => {
    var _state$settings2;
    return (_state$settings2 = state.settings) === null || _state$settings2 === void 0 || (_state$settings2 = _state$settings2.settings) === null || _state$settings2 === void 0 || (_state$settings2 = _state$settings2.configuration) === null || _state$settings2 === void 0 ? void 0 : _state$settings2.highResolutionImages;
  }
}, ['notification', 'form', 'formModal']);